import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnchorDocType, ReferenceType } from '../../../../../backend/src/types/types';
import { useGetChunksBatch } from '@/api/queries/useGetChunksBatch';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetFactDetailsBatch } from '@/api/queries/useGetFactDetailsBatch';
import { useGetKimThreadFile } from '@/api/queries/useGetKimThreadFile';
import { useGetKimThreadFileChunksBatch } from '@/api/queries/useGetKimThreadFileChunksBatch';
import { ThreadFileDownloadContext } from '@/new/components/pdf-viewer/components/download-icon';
import { BoundingBox, BoundingBoxWithId } from '@/types';

/**
 * Props the layout needs to feed straight into `<PDFViewer>`. For an existing
 * case document, only `docId` is set — PDFViewer fetches the file itself.
 * For an uploaded thread file we pre-resolve the URL + metadata since the file
 * lives outside the docs table.
 */
export interface AnchorDocViewerProps {
  docId: string;
  fileUrl?: string;
  fileMetadata?: { name?: string; extension?: string };
  fileName?: string;
  threadFile?: ThreadFileDownloadContext;
}

/**
 * A reference an item points at. Only the types that can resolve to a location
 * on the anchor doc are highlightable — facts, chunks, and thread-file chunks.
 * Other reference types (docs, entities, …) are harmless to pass and ignored.
 */
export interface AnchorDocReference {
  id: string;
  refType: ReferenceType;
}

/**
 * The minimum shape the hook needs per item — an id plus the references the
 * item points at. Anything else (description, name, etc.) lives on the
 * consumer's domain object.
 */
export interface AnchorDocFindingsItem {
  id: string;
  references: AnchorDocReference[];
}

/**
 * A reference that resolves to one or more bounding boxes on the anchor doc.
 * Normalises facts, chunks, and thread-file chunks into a single shape so the
 * consumer can render page labels without knowing the underlying source.
 */
export interface AnchorReference {
  id: string;
  refType: ReferenceType;
  boundingBoxes: BoundingBox[];
}

export interface UseAnchorDocFindingsArgs {
  matterId: string | undefined;
  subsetId: string | undefined;
  // Thread file lookup is Kim-specific; only required when anchorDocType is `ThreadFile`.
  threadId?: string;
  anchorDocId: string | null | undefined;
  anchorDocType: AnchorDocType | null | undefined;
  items: AnchorDocFindingsItem[];
}

export interface UseAnchorDocFindingsResult {
  // Display
  anchorDocName: string;
  isLoadingAnchorDoc: boolean;
  // PDFViewer-ready props. `null` while loading an uploaded thread file
  anchorDoc: AnchorDocViewerProps | null;

  // PDF highlights. `highlights` are keyed by item id so every box belonging to
  // an item greens together; `activeHighlightId` is the active item's id.
  highlights: BoundingBoxWithId[];
  activeHighlightId: string | null;

  // Per-item resolution: the references that land on the anchor doc, keyed by item id.
  anchorReferencesByItemId: Map<string, AnchorReference[]>;
  sortedItemIds: string[];

  // Active selection
  activeItemId: string | null;
  setActiveItemId: (id: string | null) => void;
  // Returns true if a matching item was found and activated.
  selectItemByReferenceId: (referenceId: string) => boolean;

  // Reference drawer
  drawerReference: { id: string; refType: ReferenceType } | null;
  setDrawerReference: (ref: { id: string; refType: ReferenceType } | null) => void;
  // Description chip-click: references on the anchor doc scroll/highlight there; all other
  // references (including off-doc ones) open the left drawer.
  handleDescriptionReferenceClick: (id: string, refType: ReferenceType) => void;
}

/**
 * Owns all the data plumbing for a "anchor doc + linked items" view. Consumers
 * provide a flat list of `{ id, factIds[] }` and get back highlights, anchor
 * facts per item, sort order, and active-selection / drawer state.
 *
 * The hook is intentionally agnostic to what an "item" represents — Kim's
 * verification viewer wraps `DocumentInconsistenciesBlock.content`, but a doc-
 * scoped inconsistency list could feed it the same shape from a different API.
 */
export function useAnchorDocFindings({
  matterId,
  subsetId,
  threadId,
  anchorDocId,
  anchorDocType,
  items,
}: UseAnchorDocFindingsArgs): UseAnchorDocFindingsResult {
  // A doc can either be a matter file in Wexler or a thread file uploaded to Kim
  const isMatterDoc = anchorDocType === AnchorDocType.MatterFile;

  // Fetch these two types of docs
  const { data: existingDoc, isLoading: isLoadingExistingDoc } = useGetDoc(
    isMatterDoc ? matterId : undefined,
    isMatterDoc && anchorDocId ? anchorDocId : null,
    subsetId,
  );
  const threadFileQuery = useGetKimThreadFile({
    matterId,
    subsetId,
    threadId,
    fileId: !isMatterDoc && anchorDocId ? anchorDocId : null,
  });

  // Split the items' references by type so each can be resolved via its own
  // batch query (facts, matter-doc chunks, and thread-file chunks each live in
  // a different table and carry their bounding boxes differently).
  const { factIds, chunkIds, threadFileChunkIds } = useMemo(() => {
    const facts = new Set<string>();
    const chunks = new Set<string>();
    const threadFileChunks = new Set<string>();
    for (const item of items) {
      for (const ref of item.references) {
        if (ref.refType === ReferenceType.Fact) facts.add(ref.id);
        else if (ref.refType === ReferenceType.Chunk) chunks.add(ref.id);
        else if (ref.refType === ReferenceType.ThreadFileChunk) threadFileChunks.add(ref.id);
      }
    }
    return {
      factIds: Array.from(facts),
      chunkIds: Array.from(chunks),
      threadFileChunkIds: Array.from(threadFileChunks),
    };
  }, [items]);

  const { data: facts } = useGetFactDetailsBatch(matterId, subsetId, factIds);
  const { data: chunks } = useGetChunksBatch(matterId, chunkIds);
  const { data: threadFileChunks } = useGetKimThreadFileChunksBatch({
    matterId,
    subsetId,
    threadId,
    chunkIds: threadFileChunkIds,
  });

  // Resolve every reference id to its bounding boxes on the anchor doc. A
  // reference that doesn't live on the anchor doc simply isn't in the map.
  const anchorBoxesByRefId = useMemo(() => {
    const map = new Map<string, BoundingBox[]>();
    if (!anchorDocId) return map;

    for (const fact of facts ?? []) {
      const loc = fact.locations.find((l) => l.docId === anchorDocId);
      if (loc?.boundingBoxes.length) map.set(fact.id, loc.boundingBoxes);
    }
    for (const chunk of chunks) {
      if (chunk.docId === anchorDocId && chunk.boundingBoxes.length) map.set(chunk.id, chunk.boundingBoxes);
    }
    // Thread-file chunks hang off a thread file; `file.id` is the anchor doc id
    // when the anchor is the uploaded file the chunk belongs to.
    for (const file of threadFileChunks) {
      if (file.id === anchorDocId && file.chunk) map.set(file.chunk.id, [file.chunk.boundingBox]);
    }
    return map;
  }, [anchorDocId, facts, chunks, threadFileChunks]);

  const anchorReferencesByItemId = useMemo(() => {
    const map = new Map<string, AnchorReference[]>();
    if (!anchorDocId) return map;
    for (const item of items) {
      const anchorRefs: AnchorReference[] = [];
      const seen = new Set<string>();
      for (const ref of item.references) {
        if (seen.has(ref.id)) continue;
        const boundingBoxes = anchorBoxesByRefId.get(ref.id);
        if (boundingBoxes?.length) {
          anchorRefs.push({ id: ref.id, refType: ref.refType, boundingBoxes });
          seen.add(ref.id);
        }
      }
      if (anchorRefs.length > 0) map.set(item.id, anchorRefs);
    }
    return map;
  }, [items, anchorBoxesByRefId, anchorDocId]);

  // Highlights are keyed by *item* id, not reference id: an item can cite several
  // references (each with several boxes) and all of them should turn green together
  // when the item is active. PDFViewer greens every box whose id matches the active
  // id, so sharing the item id across an item's boxes lights them up as a group.
  const highlights = useMemo(() => {
    const coordinates: BoundingBoxWithId[] = [];
    for (const [itemId, anchorRefs] of anchorReferencesByItemId) {
      for (const ref of anchorRefs) {
        for (const box of ref.boundingBoxes) coordinates.push({ ...box, id: itemId });
      }
    }
    coordinates.sort((a, b) => a.pageNumber - b.pageNumber || a.top - b.top);
    return coordinates;
  }, [anchorReferencesByItemId]);

  // Sort the items on the right by their position in the anchor doc
  const sortedItemIds = useMemo(() => {
    return [...items]
      .sort((a, b) => {
        const aBox = getEarliestAnchorBox(anchorReferencesByItemId.get(a.id));
        const bBox = getEarliestAnchorBox(anchorReferencesByItemId.get(b.id));
        if (!aBox && !bBox) return 0;
        if (!aBox) return 1;
        if (!bBox) return -1;
        return aBox.pageNumber - bBox.pageNumber || aBox.top - bBox.top;
      })
      .map((item) => item.id);
  }, [items, anchorReferencesByItemId]);

  const [activeItemId, setActiveItemIdState] = useState<string | null>(null);
  // Until something explicitly selects an item (card click, highlight click,
  // chip click), keep the default pinned to the first item by doc position.
  // `sortedItemIds` settles asynchronously as the fact/chunk batches resolve —
  // before the bounding boxes arrive the order is just the raw item order, so
  // latching the first id once would scroll the doc to the wrong highlight.
  const hasExplicitSelectionRef = useRef(false);

  const setActiveItemId = useCallback((id: string | null) => {
    hasExplicitSelectionRef.current = id !== null;
    setActiveItemIdState(id);
  }, []);

  useEffect(() => {
    if (hasExplicitSelectionRef.current) return;
    if (sortedItemIds.length > 0) {
      setActiveItemIdState(sortedItemIds[0]);
    }
  }, [sortedItemIds]);

  // The active doc highlight is item-keyed (see `highlights`), so the green /
  // scroll target is simply the active item id.
  const activeHighlightId = activeItemId;

  const referenceHasAnchorLocation = useCallback(
    (referenceId: string): boolean => anchorBoxesByRefId.has(referenceId),
    [anchorBoxesByRefId],
  );

  // Find the item that cites a given reference and make it active — used when a
  // reference (highlight or description chip) on the anchor doc is clicked.
  const selectItemByReferenceId = useCallback(
    (referenceId: string): boolean => {
      if (!referenceHasAnchorLocation(referenceId)) return false;

      for (const [itemId, anchorRefs] of anchorReferencesByItemId.entries()) {
        if (anchorRefs.some((r) => r.id === referenceId)) {
          hasExplicitSelectionRef.current = true;
          setActiveItemIdState(itemId);
          return true;
        }
      }

      return false;
    },
    [anchorReferencesByItemId, referenceHasAnchorLocation],
  );

  const [drawerReference, setDrawerReference] = useState<{ id: string; refType: ReferenceType } | null>(null);

  const handleDescriptionReferenceClick = useCallback(
    (id: string, refType: ReferenceType) => {
      if (referenceHasAnchorLocation(id)) {
        setDrawerReference(null);
        selectItemByReferenceId(id);
        return;
      }
      setDrawerReference({ id, refType });
    },
    [referenceHasAnchorLocation, selectItemByReferenceId],
  );

  const anchorDocName = isMatterDoc ? (existingDoc?.file?.name ?? '') : (threadFileQuery.data?.name ?? '');
  const isLoadingAnchorDoc = isMatterDoc ? isLoadingExistingDoc : threadFileQuery.isLoading;

  const anchorDoc = useMemo<AnchorDocViewerProps | null>(() => {
    if (!anchorDocId) return null;

    if (isMatterDoc) {
      return { docId: anchorDocId };
    }

    const tf = threadFileQuery.data;
    if (!tf) return null;
    return {
      docId: tf.id,
      fileUrl: tf.url,
      fileMetadata: { name: tf.name, extension: tf.extension },
      fileName: tf.name,
      threadFile:
        matterId && subsetId && threadId
          ? { matterId, subsetId, threadId, fileId: tf.id, fileName: tf.name }
          : undefined,
    };
  }, [anchorDocId, isMatterDoc, threadFileQuery.data, matterId, subsetId, threadId]);

  return {
    anchorDocName,
    isLoadingAnchorDoc,
    anchorDoc,
    highlights,
    activeHighlightId,
    anchorReferencesByItemId,
    sortedItemIds,
    activeItemId,
    setActiveItemId,
    selectItemByReferenceId,
    drawerReference,
    setDrawerReference,
    handleDescriptionReferenceClick,
  };
}

function getEarliestAnchorBox(
  anchorRefs: AnchorReference[] | undefined,
): { pageNumber: number; top: number } | undefined {
  if (!anchorRefs?.length) return undefined;

  let earliest: { pageNumber: number; top: number } | undefined;
  for (const ref of anchorRefs) {
    for (const box of ref.boundingBoxes) {
      if (
        !earliest ||
        box.pageNumber < earliest.pageNumber ||
        (box.pageNumber === earliest.pageNumber && box.top < earliest.top)
      ) {
        earliest = box;
      }
    }
  }
  return earliest;
}
